import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { confirmEmailChange } from '../api/auth'
import { apiRequest } from '../api/client'
import { TokenActionCard, type TokenActionState } from '../components/TokenActionCard'
import { useT } from '../i18n/context'
import { Button } from '../components/ui/Button'
import { useAuthStore } from '../stores/auth'
import type { UserProfile } from '../types/api'

export function ConfirmEmailChangePage() {
    const t = useT()
    const [params] = useSearchParams()
    const token = params.get('token') ?? ''
    const [state, setState] = useState<TokenActionState>('loading')
    const started = useRef(false)

    useEffect(() => {
        if (started.current || !token) return
        started.current = true
        confirmEmailChange(token)
            .then(async () => {
                setState('success')
                if (useAuthStore.getState().status === 'authenticated') {
                    try {
                        const profile = await apiRequest<UserProfile>(
                            '/users/profile',
                        )
                        useAuthStore.getState().setUser(profile)
                    } catch {
                        /* Profil-Reload ist optional */
                    }
                }
            })
            .catch(() => setState('error'))
    }, [token])

    const authenticated =
        useAuthStore(s => s.status) === 'authenticated'

    if (!token) {
        return (
            <TokenActionCard state='error' title={t('emailChange.invalidTitle')}>
                <p>{t('emailChange.noToken')}</p>
                <FooterLink />
            </TokenActionCard>
        )
    }

    return (
        <TokenActionCard
            state={state}
            title={
                state === 'success'
                    ? t('emailChange.successTitle')
                    : state === 'error'
                      ? t('emailChange.errorTitle')
                      : t('emailChange.confirming')
            }
        >
            {state === 'error' && (
                <p>{t('emailChange.errorDesc')}</p>
            )}
            {state === 'success' && authenticated && (
                <Link to='/settings'>
                    <Button variant='secondary'>{t('emailChange.toSettings')}</Button>
                </Link>
            )}
            {!authenticated && <FooterLink />}
        </TokenActionCard>
    )
}

function FooterLink() {
    const t = useT()
    return (
        <Link
            to='/login'
            className='inline-block text-xs font-medium text-indigo-700 dark:text-indigo-400 hover:underline'
        >
            {t('emailChange.toLogin')}
        </Link>
    )
}
